import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@zoom/react-ui";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "@utils/axiosInstance";
import CustomDataGridToolBar from "@components/CustomDataGridToolBar";
import { SIDEBAR_ITEMS } from "@utils/constants";
import { getFormattedAmount } from "@utils/utils";

const MAX_ACCOUNT_IDS = 5;

const splitApproverNames = (value) => {
  if (!value) return [];
  if (value === "Role pool") return [value];

  return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
};

const ApproverNamesCell = ({ value }) => {
  const names = splitApproverNames(value);

  if (!names.length) return null;

  return (
      <Box
          sx={{
            py: 0.5,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
          }}
      >
        {names.map((name, index) => (
            <Typography
                key={`${name}-${index}`}
                variant="body2"
                sx={{
                  lineHeight: 1.4,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
            >
              {name}
            </Typography>
        ))}
      </Box>
  );
};

const columns = [
  {
    field: "tabLabel",
    headerName: "Tab",
    flex: 1.2,
    minWidth: 180,
    sortable: false,
    filterable: false,
    renderCell: (params) =>
        SIDEBAR_ITEMS[params.row.tab] ||
        params.row.tabLabel ||
        params.row.tab,
  },
  {
    field: "accountNumber",
    headerName: "A#",
    flex: 0.8,
    minWidth: 120,
    sortable: false,
    filterable: false,
  },
  {
    field: "name",
    headerName: "Account Name",
    flex: 1.1,
    minWidth: 160,
    sortable: false,
    filterable: false,
  },
  {
    field: "zoomAccountNumber",
    headerName: "Zoom Account",
    flex: 0.8,
    minWidth: 120,
    sortable: false,
    filterable: false,
  },
  {
    field: "l1ApproverName",
    headerName: "Level 1 Approver",
    flex: 1.2,
    minWidth: 180,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
        <ApproverNamesCell value={params.value} />
    ),
  },
  {
    field: "l2ApproverName",
    headerName: "Level 2 Approver",
    flex: 1.2,
    minWidth: 180,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
        <ApproverNamesCell value={params.value} />
    ),
  },
  {
    field: "mrrUSD",
    headerName: "MRR (USD)",
    flex: 0.7,
    minWidth: 110,
    sortable: false,
    filterable: false,
    align: "right",
    headerAlign: "right",
    valueFormatter: (value) => getFormattedAmount(value),
  },
];

const parseAccountIds = (value) =>
    (value || "")
        .split(/[\s,;]+/)
        .map((item) => item.trim())
        .filter(Boolean);

/*
 * Account IDs are expected to start with a letter
 * followed by at least 6 digits.
 */
const accountIdPattern = /^[A-Za-z]\d{6,}$/;

const approverLabel = (option) => {
  if (!option) return "";

  if (option.name && option.email) {
    return `${option.name} (${option.email})`;
  }

  return option.name || option.email || "";
};

const AdminReassign = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [formOpen, setFormOpen] = useState(false);
  const [fromApprover, setFromApprover] = useState(null);
  const [toApprover, setToApprover] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [tableSearch, setTableSearch] = useState("");

  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
  });

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /*
   * Debounce the table search.
   */
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = searchInput
          .trim()
          .replace(/\*+$/g, "")
          .trim();

      setTableSearch(next);

      setPagination((current) => ({
        ...current,
        page: 0,
      }));
    }, 300);

    return () => clearTimeout(handle);
  }, [searchInput]);

  const searchTokens = useMemo(
      () => parseAccountIds(tableSearch),
      [tableSearch]
  );

  const looksLikeAccountIds =
     searchTokens.length >= 1 &&
      searchTokens.every((token) =>
             accountIdPattern.test(token)
      );

  const tooManyAccounts =
      looksLikeAccountIds &&
      searchTokens.length > MAX_ACCOUNT_IDS;

  const duplicateAccounts =
      looksLikeAccountIds &&
      searchTokens.length !==

      new Set(
        searchTokens.map((id) => id.toLowerCase())
      ).size;

  const invalidAccounts =
      tooManyAccounts || duplicateAccounts;

  /*
  * The actual account IDs to query with, once validated.
  */
  const accountIds =
      looksLikeAccountIds && !invalidAccounts
          ? searchTokens : [];
  /*
   * Keep the existing API format that works for multiple
   * account IDs.
   */
  const accountQueryValue = useMemo(
      () =>
          invalidAccounts
              ? ""
              : accountIds.join(","),
      [accountIds, invalidAccounts]
  );

  /*
   * IMPORTANT:
   *
   * If Account IDs or table search changes,
   * any previous selection is no longer valid.
   */
  useEffect(() => {
    setSelectAll(false);
    setSelectedRowIds([]);
  }, [accountQueryValue, tableSearch]);

  /*
   * Search bar handler.
   *
   * If the user enters multiple account-ID-like values,
   * route them into the Account IDs field instead of
   * treating the entire value as free-text search.
   *
   * will populate the Account IDs field.
   */
  const handleSearchChange = (event) => {
    const raw = event.target.value;
    /*
     * Normal free-text search.
     */
    setSearchInput(raw);

    setSelectedRowIds([]);
    setSelectAll(false);

    setPagination((current) => ({
      ...current,
      page: 0,
    }));
  };

  const { data: approverData } = useQuery({
    queryKey: ["admin-approvers"],

    queryFn: async () =>
        await axiosInstance.get(
            "/operationmonitor/api/v1/admin/approvers"
        ),

    refetchOnWindowFocus: false,
    enabled: formOpen,
  });

  const approvers =
      approverData?.data?.data || [];

  const fromEmail = fromApprover?.email;
  const toEmail = toApprover?.email;

  /*
   * Pending approvals query.
   *
   * accountNumbers is an array because the request
   * serializer converts it to the existing API format.
   */
  const {
    data,
    isFetching,
    refetch,
    isFetched,
  } = useQuery({
    queryKey: [
      "admin-pending-approvals",
      fromEmail,
      accountQueryValue,
      tableSearch,
      pagination.page,
      pagination.pageSize,
    ],

    queryFn: async () =>
        await axiosInstance.get(
            "/operationmonitor/api/v1/admin/pending-approvals",
            {
              params: {
                fromEmail,

                /*
                 * Multi-account search now reaches this
                 * existing accountNumbers parameter.
                 */
                accountNumbers: accountIds.length
                    ? accountIds
                    : undefined,

                /*
                 * Only used for normal free-text search.
                 */
                searchTerm: accountIds.length
                    ? undefined
                    : (tableSearch || undefined),

                offset: pagination.page + 1,
                limit: pagination.pageSize,
              },
              paramsSerializer: {
                indexes: null,
              },
            }
        ),

    enabled:
        formOpen &&
        Boolean(fromEmail) &&
        !invalidAccounts,

    refetchOnWindowFocus: false,
  });

  const payload =
      data?.data?.data || {};

  const rows =
      payload.rows || [];

  const total =
      payload.total || 0;

  /*
   * When Select All is enabled, all rows matching
   * the CURRENT filters are selected.
   */
  const selectedCount = selectAll
      ? total
      : selectedRowIds.length;

  /*
   * Account IDs are also a filter, so they count as search.
   */
  const hasSearch = Boolean(
      searchInput.trim() ||
      tableSearch ||
      accountIds.length
  );

  const showTable =
      Boolean(fromEmail) &&
      !invalidAccounts &&
      (total > 0 || hasSearch);

  const showEmpty =
      Boolean(fromEmail) &&
      !invalidAccounts &&
      isFetched &&
      !isFetching &&
      total === 0;

  const requirementsMatch =
      Boolean(fromEmail) &&
      Boolean(toEmail) &&
      fromEmail.toLowerCase() !==
      toEmail.toLowerCase() &&
      !invalidAccounts &&
      selectedCount > 0;

  const confirmCopy = selectAll
      ? `Reassign all ${total} pending row${
          total === 1 ? "" : "s"
      } from ${fromApprover?.name} to ${
          toApprover?.name
      }.`
      : `Reassign ${selectedRowIds.length} selected row${
          selectedRowIds.length === 1
              ? ""
              : "s"
      } from ${fromApprover?.name} to ${
          toApprover?.name
      }.`;

  const resetForm = () => {
    setAccountInput("");
    setFromApprover(null);
    setToApprover(null);

    setSearchInput("");
    setTableSearch("");

    setPagination({
      page: 0,
      pageSize: 10,
    });

    setSelectedRowIds([]);
    setSelectAll(false);

    setConfirmOpen(false);
  };

  const closeForm = () => {
    if (submitting) return;

    setFormOpen(false);
    resetForm();
  };

  const submitReassign = async () => {
    if (!requirementsMatch) return;

    setSubmitting(true);

    try {
      const response =
          await axiosInstance.post(
              "/operationmonitor/api/v1/admin/reassign",
              {
                mode: selectAll
                    ? "ALL"
                    : "SELECTED",

                fromEmail,
                toEmail,

                /*
                 * Account IDs remain an array for the POST API.
                 */
                ...(accountIds.length
                    ? {
                      accountNumbers:
                      accountIds,
                    }
                    : {}),

                  ...(!accountIds.length && tableSearch
                    ? {
                      searchTerm:
                      tableSearch,
                    }
                    : {}),

                ...(!selectAll && {
                  ids: selectedRowIds,
                }),
              }
          );

      const result =
          response?.data?.data || {};

      enqueueSnackbar(
          `Updated L1: ${
              result.updatedL1 || 0
          }, L2: ${
              result.updatedL2 || 0
          }`,
          {
            variant: "success",
          }
      );

      setSelectedRowIds([]);
      setSelectAll(false);
      setConfirmOpen(false);

      refetch();
    } catch (error) {
      enqueueSnackbar(
          error?.response?.data?.message ||
          "Failed to reassign approvers. Please try again.",
          {
            variant: "error",
          }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderApproverOption = (
      props,
      option
  ) => {
    const { key, ...liProps } = props;

    return (
        <li
            key={key || option.email}
            {...liProps}
        >
          <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                py: 0.25,
                minWidth: 0,
              }}
          >
            <Typography
                variant="body2"
                component="span"
                noWrap
            >
              {option.name}
            </Typography>

            <Typography
                variant="caption"
                color="text.secondary"
                component="span"
                noWrap
            >
              {option.email}
            </Typography>
          </Box>
        </li>
    );
  };

  return (
      <Box>
        <Box
            sx={{
              display: "flex",
              justifyContent:
                  "space-between",
              alignItems: "center",
              gap: 2,
            }}
        >
          <Typography
              variant="h6"
              className={classNames(
                  "operations-container-title",
                  "operations-3d-title"
              )}
              sx={{
                margin: 0,
                lineHeight: 1.3,
              }}
          >
            Reassign pending
          </Typography>

          <Button
              type="primary"
              onClick={() =>
                  setFormOpen(true)
              }
          >
            Reassign Pending Tasks
          </Button>
        </Box>

        <Dialog
            open={formOpen}
            onClose={closeForm}
            maxWidth="xl"
            fullWidth
        >
          <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent:
                    "space-between",
                pr: 1,
              }}
          >
            Reassign pending tasks

            <IconButton
                aria-label="Close"
                onClick={closeForm}
                disabled={submitting}
                size="small"
                sx={{
                  color:
                      "text.secondary",
                }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1fr auto 1fr",
                  },
                  gap: 2,
                  alignItems: "start",
                  marginTop: 1,
                  marginBottom: 2,
                }}
            >
              <Autocomplete
                  options={approvers}
                  value={fromApprover}
                  onChange={(
                      _event,
                      value
                  ) => {
                    setFromApprover(value);

                    setSelectedRowIds(
                        []
                    );

                    setSelectAll(false);

                    setSearchInput("");
                    setTableSearch("");

                    setPagination(
                        (current) => ({
                          ...current,
                          page: 0,
                        })
                    );
                  }}
                  getOptionLabel={
                    approverLabel
                  }
                  isOptionEqualToValue={(
                      option,
                      value
                  ) =>
                      option?.email ===
                      value?.email
                  }
                  renderOption={
                    renderApproverOption
                  }
                  renderInput={(
                      params
                  ) => (
                      <TextField
                          {...params}
                          label="From"
                          required
                          size="small"
                          helperText={
                              fromApprover?.role ||
                              "Required"
                          }
                      />
                  )}
              />

              <Box
                  sx={{
                    display: {
                      xs: "none",
                      md: "flex",
                    },
                    height: 40,
                    alignItems:
                        "center",
                    color: "#384988",
                  }}
              >
                <ArrowForwardIcon fontSize="small" />
              </Box>

              <Autocomplete
                  options={approvers.filter(
                      (option) =>
                          option.email !==
                          fromApprover?.email
                  )}
                  value={toApprover}
                  onChange={(
                      _event,
                      value
                  ) =>
                      setToApprover(value)
                  }
                  getOptionLabel={
                    approverLabel
                  }
                  isOptionEqualToValue={(
                      option,
                      value
                  ) =>
                      option?.email ===
                      value?.email
                  }
                  renderOption={
                    renderApproverOption
                  }
                  renderInput={(
                      params
                  ) => (
                      <TextField
                          {...params}
                          label="To"
                          required
                          size="small"
                          helperText={
                              toApprover?.role ||
                              "Required"
                          }
                      />
                  )}
              />
            </Box>

            {!fromEmail && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                  Select From to load
                  pending tasks.
                </Typography>
            )}

            {fromEmail &&
                isFetching &&
                total === 0 &&
                !hasSearch && (
                    <Box
                        sx={{
                          display: "flex",
                          justifyContent:
                              "center",
                          padding: 4,
                        }}
                    >
                      <CircularProgress
                          size="2.5rem"
                      />
                    </Box>
                )}

            {showEmpty && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      paddingTop: 1,
                    }}
                >
                  No pending tasks
                  matching the current
                  filters.
                </Typography>
            )}

            {showTable && (
                <div className="searchbar-wrapper">
                  <div className="searchbar-container">
                    <FormControlLabel
                        sx={{
                          marginRight: 0,
                          minWidth: "110px",
                        }}
                        control={
                          <Checkbox
                              checked={
                                selectAll
                              }
                              onChange={(
                                  event
                              ) => {
                                const checked =
                                    event
                                        .target
                                        .checked;

                                setSelectAll(
                                    checked
                                );

                                setSelectedRowIds(
                                    []
                                );
                              }}
                              disabled={
                                  total === 0
                              }
                          />
                        }
                        label="Select all"
                    />

                    <Box
                        sx={{
                          display: "flex",
                          flexWrap:
                              "wrap",
                          flex: 1,
                        }}
                        className="search_input_container"
                    >
                      <TextField
                          placeholder="Search by name, email, or account #"
                          value={
                            searchInput
                          }
                          onChange={
                            handleSearchChange
                          }
                          size="small"
                          sx={{
                            minWidth: 260,
                            flex: 1,
                            maxWidth: 420,
                            margin: 1,
                          }}
                          InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                          }}
                      />

                      <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display:
                                "flex",
                            alignItems:
                                "center",
                            margin: 1,
                          }}
                      >
                        {selectedCount >
                        0
                            ? `${selectedCount} selected`
                            : hasSearch
                                ? `${total} matching`
                                : `${total} pending`}
                      </Typography>
                    </Box>

                    <Button
                        id="searchbar-approve-button"
                        type="primary"
                        size="large"
                        className="apprv_rjct_btn"
                        disabled={
                            !requirementsMatch ||
                            submitting
                        }
                        onClick={() =>
                            setConfirmOpen(
                                true
                            )
                        }
                    >
                      Reassign
                    </Button>
                  </div>

                  <div className="datagrid-parent-container">
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) =>
                            row.id
                        }
                        columnHeaderHeight={
                          70
                        }
                        getRowHeight={() =>
                            "auto"
                        }
                        getEstimatedRowHeight={() =>
                            72
                        }
                        pageSizeOptions={[
                          10,
                          20,
                          50,
                        ]}
                        pagination
                        paginationMode="server"
                        rowCount={total}
                        paginationModel={
                          pagination
                        }
                        onPaginationModelChange={
                          setPagination
                        }
                        checkboxSelection={
                          !selectAll
                        }
                        disableRowSelectionOnClick
                        rowSelectionModel={
                          selectAll
                              ? []
                              : selectedRowIds
                        }
                        onRowSelectionModelChange={
                          (
                              newSelection
                          ) => {
                            setSelectedRowIds(
                                newSelection
                            );

                            setSelectAll(
                                false
                            );
                          }
                        }
                        loading={
                          isFetching
                        }
                        slots={{
                          toolbar:
                          CustomDataGridToolBar,
                        }}
                        keepNonExistentRowsSelected
                        sx={{
                          width: "100%",
                          height: 480,
                          "& .MuiDataGrid-columnHeaders":
                              {
                                backgroundColor:
                                    "#f6f6f6",
                                fontWeight:
                                    "bold",
                              },
                          "& .MuiDataGrid-cell":
                              {
                                alignItems:
                                    "flex-start",
                                py: 1,
                                whiteSpace:
                                    "normal",
                                lineHeight:
                                    1.4,
                              },
                        }}
                    />
                  </div>
                </div>
            )}
          </DialogContent>

          <DialogActions
              sx={{
                padding:
                    "8px 24px 20px",
              }}
          >
            <Button
                disabled={submitting}
                onClick={closeForm}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
            open={confirmOpen}
            onClose={() =>
                !submitting &&
                setConfirmOpen(false)
            }
            maxWidth="sm"
            fullWidth
        >
          <DialogTitle
              sx={{
                paddingBottom: 1,
              }}
          >
            Confirm reassignment
          </DialogTitle>

          <DialogContent>
            <Typography>
              {confirmCopy}
            </Typography>

            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  marginTop: 1.5,
                }}
            >
              Historical records
              will not change.
            </Typography>
          </DialogContent>

          <DialogActions
              sx={{
                padding:
                    "8px 24px 20px",
                gap: 1,
              }}
          >
            <Button
                disabled={submitting}
                onClick={() =>
                    setConfirmOpen(false)
                }
            >
              Cancel
            </Button>

            <Button
                type="primary"
                disabled={submitting}
                onClick={
                  submitReassign
                }
            >
              Reassign
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default AdminReassign;
